import { z } from 'zod';
import { jsonValueSchema } from './json.ts';

export const interactionEventSchema = z.object({
  id: z.string().min(1),
  type: z.string().min(1),
  projectId: z.string().min(1),
  pageId: z.string().min(1),
  sourceBlockId: z.string().min(1),
  createdAt: z.string().datetime(),
  payload: jsonValueSchema,
}).strict();

export const selectionPayloadSchema = z.object({
  datasetId: z.string().min(1).optional(),
  entityIds: z.array(z.string().min(1)).default([]),
  mode: z.enum(['replace', 'toggle', 'clear']).default('replace'),
}).strict();

export const parameterChangePayloadSchema = z.object({
  runtimeId: z.string().min(1),
  parameter: z.string().min(1),
  value: jsonValueSchema,
}).strict();

export const entitySelectionPayloadSchema = z.object({
  datasetId: z.string().min(1).optional(),
  entityId: z.string().min(1),
  mode: z.enum(['replace', 'toggle']).default('replace'),
}).strict();

export const runtimeControlPayloadSchema = z.object({
  runtimeId: z.string().min(1),
  command: z.enum(['start', 'pause', 'resume', 'stop', 'reset', 'step']),
}).strict();

export const runtimeTargetPayloadSchema = z.object({
  runtimeId: z.string().min(1),
}).strict();

export const runtimeSpeedChangePayloadSchema = z.object({
  runtimeId: z.string().min(1),
  speed: z.number().positive().max(100),
}).strict();

export const timelineEventPayloadSchema = z.object({
  runtimeId: z.string().min(1).optional(),
  time: z.number().nonnegative(),
  cueId: z.string().min(1).optional(),
}).strict();

export type InteractionEvent = z.infer<typeof interactionEventSchema>;
export type SelectionPayload = z.infer<typeof selectionPayloadSchema>;
export type EntitySelectionPayload = z.infer<
  typeof entitySelectionPayloadSchema
>;
export type ParameterChangePayload = z.infer<
  typeof parameterChangePayloadSchema
>;
export type RuntimeControlPayload = z.infer<
  typeof runtimeControlPayloadSchema
>;
export type RuntimeTargetPayload = z.infer<typeof runtimeTargetPayloadSchema>;
export type RuntimeSpeedChangePayload = z.infer<
  typeof runtimeSpeedChangePayloadSchema
>;
export type TimelineEventPayload = z.infer<typeof timelineEventPayloadSchema>;

export function createInteractionEvent(
  input: Omit<InteractionEvent, 'id' | 'createdAt'>,
): InteractionEvent {
  return interactionEventSchema.parse({
    ...input,
    id: `interaction-${crypto.randomUUID()}`,
    createdAt: new Date().toISOString(),
  });
}
